import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import FlipCard from './FlipCard';
import DetailedItineraryPage from './DetailedItineraryPage';
import TrekDetailPage from './TrekDetailPage';
import { CombinedItinerary } from '../api/itinerary';

// ══════════ View State ══════════
type TestView = 'cards' | 'journey-detail' | 'trek-detail';

// ══════════ Mock Data (matches data_source/test_data/) ══════════

const MOCK_ROAD_JOURNEY: CombinedItinerary = {
  id: '7D_leh_lakes',
  name: 'Leh Ladakh Lakes Tour',
  title: 'Alpine Lakes & Dark Skies: Morari Meets Hanle',
  description: 'A breathtaking road journey through Ladakh\'s stunning high-altitude lakes, ancient monasteries, and dramatic mountain passes.',
  duration: 7,
  tour_type: 'car',
  state: ['Ladakh'],
  packageDuration: '7D/6N',
  totalDistance: '950 km',
  highlights: ['Pangong Tso', 'Nubra Valley', 'Khardung La', 'Tso Moriri'],
  bestTimeToVisit: 'June - September',
  shortDescription: 'Explore the magical lakes of Ladakh',
  cardDescription: 'Cross Khardung La at 18,380 ft, sleep by Pangong\'s shores, and stargaze at India\'s highest observatory in Hanle. Explore Turtuk\'s Balti heritage and Nubra\'s stunning sand dunes.',
  trip_highlights: ['Leh Palace', 'Shanti Stupa', 'Leh Market', 'Sangam', 'Magnetic Hill', 'Pathar Sahib', 'SECMOL', 'Khardung La', 'Diskit Monastery', 'Hunder Dunes', 'Turtuk Village', 'Shyok Valley', 'Pangong Lake', 'Namshang La', 'Hanle Monastery', 'Hanle Observatory', 'Chumathang Springs', 'Nyoma Village', 'Thiksey Monastery'],
  costPerPerson: '₹24,500',
  cardImage: 'detailed_itinerary_images/7D_leh_lakes.jpg',
  pricePerPerson: {
    standard: '₹24,500',
    deluxe: '₹34,500',
    premium: '₹48,000',
  },
  interestingFacts: ['Pangong Lake changes colors throughout the day'],
  days: [
    { day: 1, title: 'Arrival in Leh', places: [], activities: ['Acclimatization', 'Leh Market walk'] },
    { day: 2, title: 'Leh Local Sightseeing', places: [], activities: ['Monastery visits'] },
    { day: 3, title: 'Leh to Nubra Valley', places: [], activities: ['Khardung La crossing'] },
    { day: 4, title: 'Nubra to Pangong Lake', places: [], activities: ['Shyok route drive'] },
    { day: 5, title: 'Pangong to Hanle', places: [], activities: ['Stargazing observatory'] },
    { day: 6, title: 'Hanle to Tso Moriri', places: [], activities: ['Lake visit'] },
    { day: 7, title: 'Tso Moriri to Leh', places: [], activities: ['Return drive'] },
  ],
};

const MOCK_TREK: CombinedItinerary = {
  id: 'kang_yatse_II',
  name: 'Kang Yatse II Peak Expedition',
  trekName: 'Kang Yatse II Peak Expedition',
  region: 'Ladakh, India',
  description: 'A challenging high-altitude expedition to the summit of Kang Yatse II (6,250m) in Ladakh. This trek combines the classic Markha Valley trail with a technical summit push.',
  duration: 14,
  tour_type: 'trek',
  state: ['Ladakh'],
  packageDuration: '14 Days',
  totalDistance: '95 km',
  difficulty: 'Challenging (Advanced)',
  maxAltitude: 6250,
  elevationGain: 2850,
  bestTimeToVisit: 'July - August',
  shortDescription: 'Summit a 6,250m peak in the heart of Ladakh',
  cardDescription: 'Summit 6,250m through crevasse fields and the shoulder route — Markha Valley to Kang Yatse II\'s eastern peak. Camp at Nimaling plateau inside Hemis National Park\'s wilderness.',
  trip_highlights: ['Markha Valley', 'Nimaling Plateau', 'Kongmaru La', 'Kang Yatse II', 'Hemis Park', 'Tigu Lakes', 'Sangam', 'Sara Village', 'Hankar'],
  costPerPerson: 'On Request',
  cardImage: 'trek_itinerary_images/ladakh/markha_valley.jpg',
  highlights: ['Kang Yatse II Summit', 'Markha Valley', 'Nimaling Plateau', 'Kongmaru La'],
  interestingFacts: ['One of the highest trekkable peaks in Ladakh'],
  days: [
    { day: 1, title: 'Arrival in Leh – Acclimatization', places: ['Leh'], activities: ['Rest and acclimatize'], altitude: { start: 3500, end: 3500 } },
    { day: 2, title: 'Leh Acclimatization & Briefing', places: ['Leh'], activities: ['Gear check', 'Briefing'], altitude: { start: 3500, end: 3500 } },
    { day: 3, title: 'Leh to Chilling to Skiu', places: ['Chilling', 'Skiu'], activities: ['Drive and trek start'], altitude: { start: 3500, end: 3400 } },
    { day: 4, title: 'Skiu to Markha', places: ['Markha'], activities: ['Trek through valley'], altitude: { start: 3400, end: 3800 } },
    { day: 5, title: 'Markha to Hankar', places: ['Hankar'], activities: ['River crossings'], altitude: { start: 3800, end: 4000 } },
    { day: 6, title: 'Hankar to Nimaling', places: ['Nimaling'], activities: ['High camp setup'], altitude: { start: 4000, end: 4700 } },
    { day: 7, title: 'Nimaling – Acclimatization Day', places: ['Nimaling'], activities: ['Acclimatization hike'], altitude: { start: 4700, end: 4700 } },
    { day: 8, title: 'Nimaling to Base Camp', places: [], activities: ['Move to base camp'], altitude: { start: 4700, end: 5200 } },
    { day: 9, title: 'Base Camp – Rest & Preparation', places: [], activities: ['Gear prep', 'Rest'], altitude: { start: 5200, end: 5200 } },
    { day: 10, title: 'Base Camp to High Camp', places: [], activities: ['Climb to high camp'], altitude: { start: 5200, end: 5600 } },
    { day: 11, title: 'Summit Day – Kang Yatse II (6,250m)', places: [], activities: ['Summit push', 'Return to base camp'], altitude: { start: 5600, end: 5200, peak: 6250 } },
    { day: 12, title: 'Base Camp to Nimaling', places: ['Nimaling'], activities: ['Descend'], altitude: { start: 5200, end: 4700 } },
    { day: 13, title: 'Nimaling to Shang Sumdo via Kongmaru La', places: ['Shang Sumdo'], activities: ['Kongmaru La crossing'], altitude: { start: 4700, end: 3650, peak: 5200 } },
    { day: 14, title: 'Shang Sumdo to Leh – Departure', places: ['Leh'], activities: ['Drive back to Leh'], altitude: { start: 3650, end: 3500 } },
  ] as any[],
};

// ══════════ Styled Components ══════════

const TestPageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding: 40px 24px;
`;

const TestPageHeader = styled.div`
  text-align: center;
  margin-bottom: 48px;

  h1 {
    font-family: 'Cormorant Garamond', serif;
    font-size: 2.2rem;
    font-weight: 500;
    color: #6B6555;
    margin: 0 0 8px 0;
  }

  p {
    font-family: 'DM Sans', sans-serif;
    font-size: 1rem;
    color: #A59D84;
    margin: 0;
  }
`;

const CardsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 48px;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const CardColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const CardLabel = styled.div`
  font-family: 'DM Sans', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  color: #9B9379;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  background: rgba(165, 157, 132, 0.12);
  padding: 6px 16px;
  border-radius: 20px;
`;

const InfoBar = styled.div`
  margin-top: 48px;
  text-align: center;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.85rem;
  color: #B8B09D;

  code {
    background: rgba(165, 157, 132, 0.15);
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
  }
`;

const DetailWrapper = styled.div`
  min-height: 100vh;
`;

// ══════════ Component ══════════

const FlipCardTestPage: React.FC = () => {
  const [flippedCards, setFlippedCards] = useState<Set<string | number>>(new Set());
  const [isMobile] = useState(() => window.innerWidth <= 768);
  const [currentView, setCurrentView] = useState<TestView>('cards');

  const handleFlip = useCallback((index: number | string) => {
    setFlippedCards(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const handleExplore = useCallback((item: CombinedItinerary) => {
    console.log('[FlipCardTest] handleExplore called:', item.name, 'tour_type:', item.tour_type);
    if (item.tour_type === 'trek') {
      setCurrentView('trek-detail');
    } else {
      setCurrentView('journey-detail');
    }
  }, []);

  const handleFrontClick = useCallback((item: CombinedItinerary) => {
    console.log('[FlipCardTest] handleFrontClick called:', item.name, 'tour_type:', item.tour_type);
    if (item.tour_type === 'trek') {
      setCurrentView('trek-detail');
    } else {
      setCurrentView('journey-detail');
    }
  }, []);

  const handleBack = useCallback(() => {
    setCurrentView('cards');
    window.scrollTo(0, 0);
  }, []);

  console.log('[FlipCardTest] Rendering view:', currentView);

  // ── Detail Page Views ──
  if (currentView === 'journey-detail') {
    return (
      <DetailWrapper>
        <DetailedItineraryPage
          itineraryId="7D_leh_lakes"
          state="ladakh"
          onBack={handleBack}
        />
      </DetailWrapper>
    );
  }

  if (currentView === 'trek-detail') {
    return (
      <DetailWrapper>
        <TrekDetailPage
          trekId="kang_yatse_II"
          state="ladakh"
          onBack={handleBack}
        />
      </DetailWrapper>
    );
  }

  // ── Cards View ──
  return (
    <TestPageContainer>
      <TestPageHeader>
        <h1>FlipCard UI/UX Test</h1>
        <p>Hover (desktop) or tap (mobile) to flip cards. Click Explore to view detail pages.</p>
      </TestPageHeader>

      <CardsRow>
        <CardColumn>
          <CardLabel>Road / Car Journey</CardLabel>
          <FlipCard
            item={MOCK_ROAD_JOURNEY}
            variant="journey"
            index="road-test"
            isFlipped={flippedCards.has('road-test')}
            isMobile={isMobile}
            onFlip={handleFlip}
            onExplore={handleExplore}
            onFrontClick={handleFrontClick}
          />
        </CardColumn>

        <CardColumn>
          <CardLabel>Trek Itinerary</CardLabel>
          <FlipCard
            item={MOCK_TREK}
            variant="trek"
            index="trek-test"
            isFlipped={flippedCards.has('trek-test')}
            isMobile={isMobile}
            onFlip={handleFlip}
            onExplore={handleExplore}
            onFrontClick={handleFrontClick}
          />
        </CardColumn>
      </CardsRow>

      <InfoBar>
        Tip: Run on a separate port with <code>PORT=3001 npm start</code>
      </InfoBar>
    </TestPageContainer>
  );
};

export default FlipCardTestPage;
